/**
 * E07-F07-T01 — global Do-Not-Source list (EER clients): wire contract.
 *
 * Spec: docs/superpowers/specs/2026-10-14-global-dns-eer-clients-design.md.
 * Table `dns_global_entries` (Prisma `DnsGlobalEntry`), endpoints under
 * `/admin/dns-clients` (routes/admin-dns-clients.ts). Reads: any signed-in user;
 * writes: admin only.
 *
 * Bodies are returned BARE (no `success` flag), so the web client's
 * `unwrapLegacySuccess` is a no-op for them. The shapes mirror the prototype DTOs
 * in packages/web/src/admin/dnsClientsModel.ts one-to-one.
 */
import { z } from 'zod';
/** Where an entry came from: pasted by an admin, or pulled from a project's client. */
export const dnsSourceSchema = z.enum(['manual', 'project_client']);
export const dnsClientEntrySchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Normalized domain, or null for a name-only entry. */
    domain: z.string().nullable(),
    /** Unique key: the domain, or `name:<normalized name>` when there is none. */
    matchKey: z.string(),
    source: dnsSourceSchema,
    /** `clients.id` for an entry pulled from a project; null for manual ones. */
    clientId: z.number().int().nullable(),
    /** Soft delete: false = removed (kept, restorable, never resurrected by a pull). */
    isActive: z.boolean(),
    /** Display name of who added it (null for automatic pulls / deleted users). */
    createdBy: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** GET /admin/dns-clients */
export const dnsClientsListResponseSchema = z.object({
    entries: z.array(dnsClientEntrySchema),
    activeCount: z.number().int(),
    removedCount: z.number().int(),
});
/** Hard cap on one paste (non-blank lines) — preview/add reject bigger bodies. */
export const DNS_PASTE_MAX_LINES = 1000;
/** POST /admin/dns-clients/preview and POST /admin/dns-clients — the pasted text. */
export const dnsPasteBodySchema = z.object({
    text: z.string().max(200_000),
});
export const dnsPasteItemSchema = z.object({
    /** 1-based line number in the pasted text. */
    line: z.number().int(),
    raw: z.string(),
    name: z.string(),
    domain: z.string().nullable(),
    matchKey: z.string(),
});
export const dnsPasteExistingSchema = dnsPasteItemSchema.extend({
    /** The entry it matches; null when it repeats an earlier line of the same paste. */
    match: z.object({ id: z.string(), name: z.string(), isActive: z.boolean() }).nullable(),
});
export const dnsPasteUnrecognizedSchema = z.object({
    line: z.number().int(),
    raw: z.string(),
    reason: z.string(),
});
/** POST /admin/dns-clients/preview */
export const dnsPastePreviewResponseSchema = z.object({
    fresh: z.array(dnsPasteItemSchema),
    existing: z.array(dnsPasteExistingSchema),
    unrecognized: z.array(dnsPasteUnrecognizedSchema),
});
/** POST /admin/dns-clients — only the NEW lines are saved. */
export const dnsAddResponseSchema = z.object({
    added: z.number().int(),
});
/**
 * POST /admin/dns-clients/pull-from-projects (E07-F07-T02) — one-off backfill of
 * the clients of existing projects. Idempotent: a repeat adds 0.
 *
 * Counts are DISTINCT LIST ENTRIES (E07-F07-T06), not project clients: several
 * CRM duplicates of one company count once, so `alreadyListed` never exceeds the
 * Active tab and `skippedRemoved` never exceeds the Removed tab.
 */
export const dnsPullResponseSchema = z.object({
    /** New entries created from project clients. */
    added: z.number().int(),
    /** Active list entries that already cover a project client. */
    alreadyListed: z.number().int(),
    /** Removed list entries that cover a project client — deliberately NOT brought back. */
    skippedRemoved: z.number().int(),
});
/**
 * POST /admin/dns-clients/pull-from-projects — optional body (E07-F07-T06).
 * `clientIds` = the «will be added» set the admin confirmed in the preview; the
 * server re-plans them (a client that became a duplicate meanwhile is skipped).
 * Omitted → every client linked to a project.
 */
export const dnsPullBodySchema = z.object({
    clientIds: z.array(z.number().int().positive()).max(10_000).optional(),
});
/** A project client the pull would add. */
export const dnsPullCandidateSchema = z.object({
    clientId: z.number().int(),
    name: z.string(),
    domain: z.string().nullable(),
});
/** An existing list entry that already covers one or more project clients. */
export const dnsPullMatchSchema = z.object({
    id: z.string(),
    name: z.string(),
    domain: z.string().nullable(),
});
/**
 * POST /admin/dns-clients/pull-from-projects/preview (E07-F07-T06) — saves
 * nothing. `alreadyListed` / `removed` are distinct entries (see above).
 */
export const dnsPullPreviewResponseSchema = z.object({
    fresh: z.array(dnsPullCandidateSchema),
    alreadyListed: z.array(dnsPullMatchSchema),
    removed: z.array(dnsPullMatchSchema),
});
/** PATCH /admin/dns-clients/:id — raw domain; normalized on save, empty → name-only. */
export const dnsClientUpdateBodySchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    domain: z.string().max(255).optional().default(''),
});
/** `:id` of a dns_global_entries row (uuid). */
export const dnsClientIdParamSchema = z.object({
    id: z.string().uuid(),
});
//# sourceMappingURL=dns-global.js.map